<template>
  <Profile
    :open="open"
    @update:open="$emit('update:open', $event)"
    :buildings="buildings"
    :user="user"
    :errors="errors"
    :busy="busy"
    @changeEmail="handleChangeEmail"
    @resendEmail="handleResendEmail"
    @changePassword="handleChangePassword"
    @updateLanguage="handleUpdateLanguage"
    @logout="handleLogout"
    @saveProfile="handleSaveProfile"
    @fileSelected="handleFileSelected"
    @fileRemoved="handleFileRemoved"
    @updateWeeklyDigest="handleUpdateWeeklyDigest"
    @clearErrors="errors = {}"
  />
</template>

<script setup>
import { Profile } from "@/SharedComponents/Profile";
import { toast } from "@/SharedComponents/Toaster";
import { Envelope } from "@/SharedComponents/Toaster/Icons";
import { router, usePage } from "@inertiajs/vue3";
import { computed, ref } from "vue";
import route from "ziggy-js";
import axios from "axios";
import { useI18n } from "vue-i18n";

defineEmits(["update:open"]);

const { t, locale } = useI18n();
const errors = ref({});

// temporary - so we can see updated user in our profile
const localUser = ref(null);

// @todo: this guy (user) needs to live in store, not here
// without that I cant see a way to update the user object across the platform
const user = computed(() => localUser.value || usePage().props.auth?.user);

const avatar = ref(null);

defineProps({
  open: { type: Boolean, default: undefined },
  buildings: { type: Array, default: () => [] },
});

const busy = ref(false);

/*
 * Event handlers, physical data updates happen here
 * at the moment is all handled directly here, but should be moved to a service
 */
async function handleChangeEmail({ form, onSuccess }) {
  busy.value = true;
  await axios
    .patch(route("settings.user.email.update"), form.data())
    .then((response) => {
      onSuccess();
      localUser.value = response.data;
      toast.info({
        message: t("toasts.profile.email.verification.sent.message"),
        description: t("toasts.profile.email.verification.sent.description"),
        icon: Envelope,
      });
      errors.value = {};
    })
    .catch((error) => {
      if (error.response?.data?.errors)
        errors.value = error.response.data.errors;
    })
    .finally(() => {
      busy.value = false;
    });
}

async function handleResendEmail() {
  busy.value = true;
  await axios
    .post(route("settings.user.email.verification.resend"))
    .then(() => {
      toast.info({
        message: t("toasts.profile.email.verification.sent.message"),
        description: t("toasts.profile.email.verification.sent.description"),
        icon: Envelope,
      });
    })
    .catch((error) => {
      if (error.response?.data?.errors)
        errors.value = error.response.data.errors;
    })
    .finally(() => {
      busy.value = false;
    });
}

async function handleChangePassword(data) {
  busy.value = true;
  await axios
    .patch(route("settings.user.password.update"), data)
    .then((response) => {
      busy.value = false;
      localUser.value = response.data;
      toast.success({
        message: t("components.profile.password.messages.success.title"),
        description: t(
          "components.profile.password.messages.success.description"
        ),
      });
      errors.value = {};
    })
    .catch((error) => {
      toast.error({
        message: t("components.profile.password.messages.error.title"),
        description: t(
          "components.profile.password.messages.error.description"
        ),
      });
      if (error.response?.data?.errors)
        errors.value = error.response.data.errors;
    })
    .finally(() => {
      busy.value = false;
    });
}

async function handleUpdateLanguage(data) {
  busy.value = true;
  await axios
    .patch(route("settings.user.language.update"), data)
    .then((response) => {
      busy.value = false;
      localUser.value = response.data;
      locale.value = localUser.value.locale;
      toast.success({
        message: t("components.profile.language.messages.success.title"),
        description: t(
          "components.profile.language.messages.success.description"
        ),
      });
      errors.value = {};
    })
    .catch((error) => {
      toast.error({
        message: t("components.profile.language.messages.error.title"),
        description: t(
          "components.profile.language.messages.error.description"
        ),
      });
      if (error.response?.data?.errors)
        errors.value = error.response.data.errors;
    })
    .finally(() => {
      busy.value = false;
    });
}

function handleLogout() {
  busy.value = true;
  router.post(
    route("logout"),
    {},
    {
      onSuccess: () => {
        localStorage.clear();
      },
      onFinish: () => {
        busy.value = false;
        window.location.reload();
      },
    }
  );
}

async function handleSaveProfile({ data, callback }) {
  busy.value = true;

  if (avatar.value) {
    const formData = new FormData();
    formData.append("avatar", avatar.value);
    await axios
      .post(route("settings.user.avatar.update"), formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .catch(() => {
        toast.error({
          message: t("components.profile.avatar.messages.error.title"),
          description: t(
            "components.profile.avatar.messages.error.description"
          ),
        });
      });
  }

  await axios
    .patch(route("settings.user.profile.update"), data)
    .then((response) => {
      busy.value = false;
      localUser.value = response.data;
      toast.success({
        message: t("components.profile.edit.messages.success.title"),
        description: t("components.profile.edit.messages.success.description"),
      });
      errors.value = {};
    })
    .catch((error) => {
      toast.error({
        message: t("components.profile.edit.messages.error.title"),
        description: t("components.profile.edit.messages.error.description"),
      });
      if (error.response?.data?.errors)
        errors.value = error.response.data.errors;
    })
    .finally(() => {
      busy.value = false;
      callback();
    });
}

function handleFileSelected(data) {
  avatar.value = data.file;
}

// @todo: add support for removing existing avatar after submit only!
function handleFileRemoved() {
  if (avatar.value) {
    avatar.value = null;
  } else if (user.value.avatar_url) {
    axios
      .delete(route("settings.user.avatar.delete"))
      .then((response) => {
        localUser.value = response.data;
        toast.success({
          message: t("components.profile.avatar.messages.delete.success.title"),
          description: t(
            "components.profile.avatar.messages.delete.success.description"
          ),
        });
      })
      .catch(() => {
        toast.error(t("components.profile.avatar.messages.delete.error"));
      });
  }
}

function handleUpdateWeeklyDigest(value) {
  axios
    .patch(route("settings.user.notifications.update"), { enabled: value })
    .then((response) => {
      localUser.value = response.data;
      toast.success(t("toasts.notifications.settings.update.success"));
    });
}
</script>
